import { useEffect, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useVoiceAgentStore } from '@stores/voiceAgentStore';
import voiceService from '@services/voice.service';
import { callClock } from '../callConsole';
import { saveWrapUp, WRAP_UP_OUTCOMES, type WrapUpOutcome } from '../wrapUp';

/**
 * After an answered call ends, whoever hung up: how did it go? The outcome
 * and the agent's notes land on the ticket as an internal note, and the ticket
 * status follows the outcome. Skippable — the ticket is never worse off.
 */
export function WrapUpDialog() {
  const wrapUp = useVoiceAgentStore((s) => s.wrapUp);
  const setWrapUp = useVoiceAgentStore((s) => s.setWrapUp);
  const [outcome, setOutcome] = useState<WrapUpOutcome>('resolved');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A new wrap-up starts from the notes typed during the call.
  useEffect(() => {
    if (!wrapUp) return;
    setOutcome('resolved');
    setNotes(wrapUp.notes);
    setError(null);
  }, [wrapUp]);

  if (!wrapUp) return null;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveWrapUp(wrapUp, outcome, notes);
      setWrapUp(null);
      useVoiceAgentStore.getState().setNotice('Wrap-up saved to the ticket.');
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'The wrap-up could not be saved');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true" aria-label="Wrap up the call">
      <div
        className="w-full max-w-md border border-[var(--co-line)] bg-[var(--co-raised)]"
        style={{ borderRadius: 14, boxShadow: 'var(--co-shadow-card)' }}
      >
        <div className="border-b border-[var(--co-line)] px-6 py-4">
          <p className="co-mono text-[10px] uppercase tracking-[.14em] text-[var(--co-ink-3)]">Call ended · wrap up</p>
          <p className="co-mono mt-1 text-[16px] font-bold text-[var(--co-ink)]">
            {voiceService.formatPhoneNumber(wrapUp.customerPhone || '') || 'Unknown caller'}
            <span className="ml-2 text-[12px] font-normal text-[var(--co-ink-3)]">{callClock(wrapUp.durationSeconds)}</span>
          </p>
        </div>

        <div className="space-y-4 px-6 py-5">
          <fieldset>
            <legend className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">Outcome</legend>
            <div className="mt-2 grid gap-2">
              {WRAP_UP_OUTCOMES.map((o) => (
                <label
                  key={o.value}
                  className="flex cursor-pointer items-center gap-3 rounded-[10px] border px-3 py-[9px]"
                  style={
                    outcome === o.value
                      ? { borderColor: 'var(--co-brass)', background: 'var(--co-brass-wash)' }
                      : { borderColor: 'var(--co-line)' }
                  }
                >
                  <input
                    type="radio"
                    name="wrap-up-outcome"
                    value={o.value}
                    checked={outcome === o.value}
                    onChange={() => setOutcome(o.value)}
                    className="accent-[var(--co-brass-ink)]"
                  />
                  <span className="text-[13px] font-semibold text-[var(--co-ink)]">{o.label}</span>
                  <span className="text-[11.5px] text-[var(--co-ink-3)]">{o.hint}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <label className="block">
            <span className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-3)]">Notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="What was agreed, what happens next…"
              className="mt-2 w-full resize-y rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 py-2 text-[13px] text-[var(--co-ink)] focus:border-[var(--co-brass)] focus:outline-none"
            />
          </label>

          {error && (
            <p role="alert" className="text-[12px]" style={{ color: 'var(--co-burgundy)' }}>
              {error}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-[var(--co-line)] px-6 py-4">
          <button
            type="button"
            onClick={() => setWrapUp(null)}
            disabled={saving}
            className="h-10 rounded-[10px] border border-[var(--co-line-strong)] px-4 text-[13px] text-[var(--co-ink-2)] hover:bg-[var(--co-selected)]"
          >
            Skip
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="flex h-10 items-center gap-2 rounded-[10px] px-4 text-[13px] font-bold text-[var(--co-brass-on)] disabled:opacity-60"
            style={{ background: 'var(--co-brass-ink)' }}
          >
            <CheckCircle2 className="h-4 w-4" />
            {saving ? 'Saving…' : 'Save to ticket'}
          </button>
        </div>
      </div>
    </div>
  );
}
